import {inject} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of} from 'rxjs';
import {catchError, finalize, map, mergeMap, switchMap} from 'rxjs/operators';
import {PlanningDraftJobChangesApiActions} from './planning-draft-job-changes.actions';
import {PlanningDraftJobChangesHttpService, DraftJobChangesPage} from '../services/planning-draft-job-changes-http.service';
import {CommonsFacadeService} from '../../../commons/angular/state/commons/commons-facade.service';
import {BatchModel} from '../../../batch/models/batch.model';

// scan/apply on a draft with nothing to process still moves the draft to a terminal state
// server-side even though the call itself errors — treat it as "finished with nothing to do".
// PLANNING_DRAFT_JOB_CHANGES_NO_JOBS_FOR_PROCESSING — renumbered 400481 -> 400483 (v2 changelist,
// the 400479/400480 slots were taken on develop; all four jobChanges codes shifted by 2).
const NO_JOBS_FOR_PROCESSING_CODE = 400483;

export const scanDraftJobChanges = createEffect(
  (actions$ = inject(Actions),
   httpService = inject(PlanningDraftJobChangesHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDraftJobChangesApiActions.scanDraftJobChanges),
      mergeMap(({projectId, draftId}) => {
        commonsFacadeService.setLoaderOn();
        return httpService.scan(projectId, draftId).pipe(
          map((batch: BatchModel) => PlanningDraftJobChangesApiActions.scanDraftJobChangesSuccess({batch})),
          catchError((error: any) => {
            if (error?.error?.code === NO_JOBS_FOR_PROCESSING_CODE) {
              return of(PlanningDraftJobChangesApiActions.scanDraftJobChangesSuccess({batch: null}));
            }
            return of(PlanningDraftJobChangesApiActions.scanDraftJobChangesFailure({error}));
          }),
          finalize(() => commonsFacadeService.setLoaderOff())
        );
      })
    );
  },
  {functional: true}
);

export const getDraftJobChangesPage = createEffect(
  (actions$ = inject(Actions),
   httpService = inject(PlanningDraftJobChangesHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDraftJobChangesApiActions.getDraftJobChangesPage),
      // switchMap: a new filter/search must drop a still-in-flight page of the old one, otherwise
      // that late response would be appended to the new list.
      switchMap(({projectId, draftId, params}) => {
        commonsFacadeService.setLoaderOn();
        return httpService.getPage(projectId, draftId, params).pipe(
          map((res: DraftJobChangesPage) => PlanningDraftJobChangesApiActions.getDraftJobChangesPageSuccess({
            list: res.content,
            totalElements: res.totalElements,
            page: params.page
          })),
          catchError((error: any) => of(PlanningDraftJobChangesApiActions.getDraftJobChangesPageFailure({error}))),
          finalize(() => commonsFacadeService.setLoaderOff())
        );
      })
    );
  },
  {functional: true}
);

export const applyDraftJobChanges = createEffect(
  (actions$ = inject(Actions),
   httpService = inject(PlanningDraftJobChangesHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDraftJobChangesApiActions.applyDraftJobChanges),
      mergeMap(({projectId, draftId, filter}) => {
        commonsFacadeService.setLoaderOn();
        return httpService.apply(projectId, draftId, filter).pipe(
          map((batch: BatchModel) => PlanningDraftJobChangesApiActions.applyDraftJobChangesSuccess({batch})),
          catchError((error: any) => {
            if (error?.error?.code === NO_JOBS_FOR_PROCESSING_CODE) {
              return of(PlanningDraftJobChangesApiActions.applyDraftJobChangesSuccess({batch: null}));
            }
            return of(PlanningDraftJobChangesApiActions.applyDraftJobChangesFailure({error}));
          }),
          finalize(() => commonsFacadeService.setLoaderOff())
        );
      })
    );
  },
  {functional: true}
);

export const dismissDraftJobChanges = createEffect(
  (actions$ = inject(Actions),
   httpService = inject(PlanningDraftJobChangesHttpService),
   commonsFacadeService = inject(CommonsFacadeService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDraftJobChangesApiActions.dismissDraftJobChanges),
      mergeMap(({projectId, draftId, filter}) => {
        commonsFacadeService.setLoaderOn();
        return httpService.dismiss(projectId, draftId, filter).pipe(
          map(() => PlanningDraftJobChangesApiActions.dismissDraftJobChangesSuccess({filter})),
          catchError((error: any) => of(PlanningDraftJobChangesApiActions.dismissDraftJobChangesFailure({error}))),
          finalize(() => commonsFacadeService.setLoaderOff())
        );
      })
    );
  },
  {functional: true}
);

export const getDraftJobChangesProcessTracking = createEffect(
  (actions$ = inject(Actions),
   httpService = inject(PlanningDraftJobChangesHttpService)
  ) => {
    return actions$.pipe(
      ofType(PlanningDraftJobChangesApiActions.getDraftJobChangesProcessTracking),
      mergeMap(({projectId, draftId, params}) => {
        return httpService.getProcessTracking(projectId, draftId, params).pipe(
          map((list) => PlanningDraftJobChangesApiActions.getDraftJobChangesProcessTrackingSuccess({list})),
          catchError((error: any) => of(PlanningDraftJobChangesApiActions.getDraftJobChangesProcessTrackingFailure({error})))
        );
      })
    );
  },
  {functional: true}
);
